import React, { useState } from 'react';
import { AlbumFrameRow, getAlbumTranscript } from '../../services/marketingApi';
import { Kpis, Modal, Pill, btn, btnTone, input, label } from '../Kit';

export const ALBUM_URL = 'https://bhuvanafortunegreens.com/album';

export type FrameFamily = 'v' | 'a' | 'y' | 't' | 's' | 'su' | 'special';

export const FAMILY_LABELS: Record<FrameFamily, string> = {
  v: 'Ad reels',
  a: 'In-house',
  y: 'YouTube',
  t: 'Testimonials',
  s: 'Suman TV',
  su: 'Suman TV (portrait)',
  special: 'Specials',
};

// `su` is matched before the single-letter families: code[0] of "su01" is 's',
// which would otherwise fold the portrait re-cuts into the landscape originals.
export const familyOf = (code: string): FrameFamily =>
  /^su\d\d$/.test(code)
    ? 'su'
    : /^[vayts]\d\d$/.test(code)
      ? (code[0] as FrameFamily)
      : 'special';

export const fmtDur = (s: number | null) =>
  s == null ? null : s < 60 ? `${s}s` : `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

export const fmtMs = (ms: number | null | undefined) =>
  ms == null ? '—' : ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`;

// Load-latency tone: fast ok, sluggish (≥1.5s) gold, slow (≥3s) warn.
export const loadTone = (ms: number | null): 'ok' | 'gold' | 'warn' | undefined =>
  ms == null ? undefined : ms >= 3000 ? 'warn' : ms >= 1500 ? 'gold' : 'ok';

/** Median load time as a pill — only for timed video frames. */
export const LoadPill: React.FC<{ f: AlbumFrameRow }> = ({ f }) =>
  f.video_url && f.stats.timing_n > 0 ? (
    <span title={`median load time (${f.stats.timing_n} views)`}>
      <Pill tone={loadTone(f.stats.load_ms_p50)}>{fmtMs(f.stats.load_ms_p50)} load</Pill>
    </span>
  ) : null;

/** A frame's poster for list rows; specials (brochure / closer) get a quiet tile. */
export const FrameThumb: React.FC<{ f: AlbumFrameRow; className?: string }> = ({ f, className = 'w-11 h-[60px]' }) =>
  f.poster_url ? (
    <span className={`relative ${className} flex-none block rounded-md overflow-hidden bg-charcoal/[0.06]`}>
      <img src={f.poster_url} alt="" loading="lazy" className="w-full h-full object-cover" />
      {f.video_url && <span className="absolute inset-0 grid place-items-center text-warm-white text-[10px] bg-charcoal/20">▶</span>}
    </span>
  ) : (
    <span className={`${className} flex-none grid place-items-center rounded-md text-[10px] font-semibold uppercase tracking-wide ${
      f.type === 'cta' ? 'bg-forest text-warm-white' : 'bg-cream text-forest'}`}>
      {f.type === 'brochure' ? 'PDF' : f.type === 'cta' ? 'CTA' : ''}
    </span>
  );

/** Frame detail overlay shared by the Album list and Album Order pages.
 * Mount with key={frame.code} so transcript/tag-input state resets per frame. */
const FrameModal: React.FC<{
  frame: AlbumFrameRow;
  onClose: () => void;
  onSaveTags: (frame: AlbumFrameRow, tags: string[]) => void;
}> = ({ frame: open, onClose, onSaveTags }) => {
  const [transcript, setTranscript] = useState<string | null>(null);
  const [newTag, setNewTag] = useState('');
  const [copied, setCopied] = useState(false);

  const addTag = () => {
    const t = newTag.trim();
    if (t && !open.tags.includes(t)) {
      onSaveTags(open, [...open.tags, t]);
      setNewTag('');
    }
  };

  return (
    <Modal
      wide
      onClose={onClose}
      title={<><span className="mr-2 font-mono text-[15px] text-graphite">{open.code}</span>{open.title ?? open.type}</>}
      footer={<>
        {open.transcript_url && transcript == null && (
          <button type="button" className={`${btn} ${btnTone.ghost}`}
                  onClick={() => getAlbumTranscript(open.code).then((t) => setTranscript(t.no_audio ? '(silent video — no transcript)' : t.text))}>
            Load transcript
          </button>
        )}
        {open.video_url && (
          <a href={open.video_url} target="_blank" rel="noopener noreferrer" className={`${btn} ${btnTone.charcoal}`}>Open video file</a>
        )}
        <button type="button" className={`${btn} ${btnTone.forest}`}
                onClick={() => { navigator.clipboard.writeText(`${ALBUM_URL}?f=${open.code}`); setCopied(true); setTimeout(() => setCopied(false), 1500); }}>
          {copied ? 'Copied' : `Copy link ?f=${open.code}`}
        </button>
      </>}
    >
      <div className="grid grid-cols-1 gap-7 md:grid-cols-[minmax(220px,320px)_1fr]">
        <div className="grid place-items-center self-start overflow-hidden rounded-lg bg-charcoal">
          {open.video_url ? (
            <video src={open.video_url} poster={open.poster_url ?? undefined} controls autoPlay className="max-h-[70vh] w-full object-contain" />
          ) : open.poster_url ? (
            <img src={open.poster_url} alt={open.code} className="max-h-[70vh] w-full object-contain" />
          ) : (
            <div className="p-10 text-[14px] text-warm-white/80">{open.type === 'brochure' ? 'Brochure offer frame' : 'Closer / book-a-visit frame'}</div>
          )}
        </div>

        <div className="min-w-0 space-y-6">
          <div className="flex flex-wrap items-center gap-1.5 text-[13px] text-graphite">
            {open.position != null ? <Pill tone="ok">Feed #{open.position}</Pill> : <Pill>Not in feed</Pill>}
            <span>{[FAMILY_LABELS[familyOf(open.code)], fmtDur(open.duration_s), open.audio].filter(Boolean).join(' · ')}</span>
          </div>

          <div className="border-y border-charcoal/10 py-4">
            <Kpis small items={[
              [open.stats.views, 'views'], [open.stats.unique_leads, 'leads'], [open.stats.likes, 'likes'],
              [open.stats.shares, 'shares'], [open.stats.brochure_opens, 'brochure opens'],
              ...(open.video_url ? [
                [fmtMs(open.stats.load_ms_p50), 'median load'], [fmtMs(open.stats.dwell_ms_p50), 'median watch'],
                [open.stats.timing_n, 'timed views'],
              ] as [React.ReactNode, string][] : []),
            ]} />
          </div>

          {(open.tag || open.caption) && (
            <div>
              <div className={label}>{open.tag ?? 'Caption'}</div>
              {open.caption && <div className="text-[14px] text-charcoal">{open.caption}</div>}
              {open.cta && <div className="mt-1.5"><Pill tone="gold">Book-a-visit pill</Pill></div>}
            </div>
          )}

          {(open.description || open.best_for || open.source) && (
            <div className="space-y-2">
              {open.description && <p className="text-[13.5px] leading-relaxed text-charcoal">{open.description}</p>}
              {open.best_for && <p className="text-[13px] text-graphite"><b className="text-charcoal">Best for:</b> {open.best_for}</p>}
              {open.source && <p className="text-[12px] text-graphite">{open.source}</p>}
            </div>
          )}

          <div>
            <label className={label} htmlFor="frame-new-tag">Tags</label>
            <div className="flex flex-wrap items-center gap-1.5">
              {open.tags.map((t) => (
                <span key={t} className="inline-flex items-center gap-1 rounded-full bg-charcoal/[0.06] pl-2.5 pr-1.5 py-0.5 text-[12px] text-charcoal">
                  {t}
                  <button type="button" onClick={() => onSaveTags(open, open.tags.filter((x) => x !== t))}
                          aria-label={`remove tag ${t}`} className="text-graphite hover:text-brick">×</button>
                </span>
              ))}
              <input
                id="frame-new-tag"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') addTag(); }}
                placeholder="Add tag ⏎"
                className={`${input} w-32 h-8`}
              />
            </div>
          </div>

          {transcript != null && (
            <div>
              <div className={label}>Transcript</div>
              <div className="max-h-56 overflow-y-auto whitespace-pre-wrap rounded-lg bg-cream px-3.5 py-2.5 text-[13px] leading-relaxed text-charcoal">
                {transcript}
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default FrameModal;
